import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForMedicalDoctorsPage() {
  return (
    <>
      <Head>
        <title>
          Featcal for Medical Doctors | Structured follow-up, better adherence
        </title>
        <meta
          name="description"
          content="Featcal helps medical doctors run structured follow-up between visits with patient-reported tracking, review-ready summaries, and higher adherence-without extra administrative overhead."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            For medical doctors
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Get a clearer picture between visits-without chasing data.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal collects patient-reported tracking and turns it into a
            structured review flow so you can intervene earlier and improve
            adherence-across weight loss, weight gain, and metabolic health.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Request a demo
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              See FAQs
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              The real challenge: follow-up between visits
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Patient-reported data is fragmented across apps and messages.",
                "It’s hard to spot adherence issues early.",
                "Staff time gets consumed by operational follow-up and routine questions.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Structured patient-reported tracking",
                desc: "Weight, meals, activity, sleep, hydration-and more-in one place, tailored to your pathway.",
              },
              {
                title: "Review-ready summaries",
                desc: "See trends and adherence gaps before the next appointment, without manual cleanup.",
              },
              {
                title: "Less operational noise",
                desc: "Automated nudges and clearer next steps reduce chasing and inbound routine questions.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Great fit for
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Obesity and metabolic health pathways with structured follow-up.",
                  "Lifestyle medicine programs focused on adherence.",
                  "Post-intervention monitoring after medication changes.",
                  "Hybrid care models where between-visit tracking matters.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What Featcal replaces
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Scattered forms, apps, and messages.",
                  "Manual summaries before appointments.",
                  "Ad-hoc follow-up calls for routine issues.",
                  "Operational noise that slows teams down.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Clinician dashboard"
          title="A clear snapshot before each consult"
          description="Review trends and adherence quickly with a structured dashboard-so follow-up stays consistent without extra admin."
          bullets={[
            "Faster review before appointments.",
            "More structured follow-up across patients.",
            "Less manual back-and-forth for your team.",
          ]}
          imageSrc="/images/customer-admin.webp"
          imageAlt="Clinician dashboard preview"
          align="left"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Want to run follow-up with less operational noise?
                </h2>
                <p className="text-slate-700 mt-2">
                  Request a demo to see Featcal in your workflow.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Request a demo
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  See FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
